import React, { useState, useCallback, useEffect } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Box from '@amzn/meridian/box';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import Icon from '@amzn/meridian/icon';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  setCurrentPage,
  setIsCreateModalOpen,
  setSelectedShift,
  setTotalPages,
  TableDataRow,
} from 'src/store/updateShiftsPageSlice';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';
import Button from '@amzn/meridian/button';

interface ShiftsTableProps {
  onDeleteShift: (shift: TableDataRow) => void;
}

const ShiftsTable = ({ onDeleteShift }: ShiftsTableProps) => {
  const dispatch = useAppDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TableDataRow>('shiftCode');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: keyof TableDataRow; sortDirection: TableSortDirection }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );
  const { tableData, searchField, currentPage, perPage } = useAppSelector((state) => state.updateShiftsPage);
  const [sortedTableData, setSortedTableData] = useState(tableData);

  useEffect(() => {
    // filter, sorting, and pagination
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      tableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id', 'daysOfWeek'],
      perPage,
    );
    setSortedTableData(paginatedData as TableDataRow[]);
    dispatch(setTotalPages(totalPages));
  }, [sortColumn, sortDirection, tableData, searchField, currentPage, perPage, dispatch]);

  useEffect(() => {
    // go back to first page when filter/sort
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection, dispatch]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="update-shifts__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="shiftCode">Shift Code</TableCell>
          <TableCell sortColumn="shiftLength">Shift Length</TableCell>
          <TableCell sortColumn="daysOfWeek">Days</TableCell>
          <TableCell sortColumn="dayOrNight">Day/Night</TableCell>
          <TableCell sortColumn="active">Active/Inactive</TableCell>
          <TableCell>Edit</TableCell>
          <TableCell>Delete</TableCell>
        </TableRow>
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.id}>
            <TableCell>{row.shiftCode}</TableCell>
            <TableCell>{row.shiftLength}</TableCell>
            <TableCell>{row.daysString}</TableCell>
            <TableCell>{row.dayOrNight || ''}</TableCell>
            <TableCell>{row.active || ''}</TableCell>
            <TableCell>
              <div
                className="update-shifts__edit-icon"
                onClick={() => {
                  dispatch(setIsCreateModalOpen(true));
                  dispatch(setSelectedShift(row));
                }}
              >
                <Icon tokens={editTokens}></Icon>
              </div>
            </TableCell>
            <TableCell>
              <Button type="icon" onClick={() => onDeleteShift(row)}>
                <Icon tokens={trashTokens} />
              </Button>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default ShiftsTable;
